// Settings links of the Agents & Models page and the global PRDs index. They state their real
// availability instead of showing illustrative data (RF-UI-16, AC-74). Memory, Inbox and
// Integrations have their own pages since wave 3; the Agents page lives in AgentsPage.tsx (D20).
import { Link } from "react-router";
import { useI18n } from "../i18n/index.tsx";
import { scopeLabel } from "../lib/format.ts";
import { listProjects } from "../api/endpoints.ts";
import { useAsync } from "../lib/useAsync.ts";
import { AGGREGATE_VIEW } from "../lib/status.ts";
import { EmptyState, ErrorBanner, LoadingBlock } from "../components/Feedback.tsx";
import { StatusPill } from "../components/Status.tsx";
import { ArrowRightIcon, CpuIcon, FileTextIcon } from "../components/icons.tsx";

/** Models, profiles per role and quotas live in Settings (the "Models & quotas" tab of the Agents page). */
export function AgentsSettingsLinks() {
  const { t } = useI18n();
  return (
    <div className="card" data-testid="agents-settings-links">
      <EmptyState
        icon={<CpuIcon />}
        title={t("agents.heading")}
        actions={
          <>
            <Link to="/settings/agents" className="btn btn--primary">
              {t("settings.tab.agents")}
            </Link>
            <Link to="/settings/models" className="btn">
              {t("settings.tab.models")}
            </Link>
            <Link to="/settings/quotas" className="btn">
              {t("settings.tab.quotas")}
            </Link>
          </>
        }
      >
        <p>{t("agents.body")}</p>
        <p className="subtle">{t("phase.f04")}</p>
      </EmptyState>
    </div>
  );
}

/** PRDs live inside projects; this index links to each project's PRD tab. */
export function PrdsIndexPage() {
  const { t } = useI18n();
  const projects = useAsync((signal) => listProjects({ limit: 100 }, signal), []);
  const list = projects.data?.items ?? [];
  return (
    <div className="page">
      <header className="page-header">
        <div className="stack" style={{ gap: 4 }}>
          <h1 className="page-title">{t("nav.prds")}</h1>
          <p className="page-subtitle">{t("prdsIndex.subtitle")}</p>
        </div>
      </header>
      {projects.loading ? (
        <LoadingBlock label={t("common.loading")} />
      ) : projects.error ? (
        <ErrorBanner error={projects.error} onRetry={projects.reload} />
      ) : list.length === 0 ? (
        <div className="card">
          <EmptyState icon={<FileTextIcon />} title={t("prdsIndex.emptyTitle")}>
            <p>{t("prdsIndex.emptyBody")}</p>
            <div className="empty-state-actions">
              <Link to="/" className="btn btn--primary">
                {t("prdsIndex.startHome")}
              </Link>
            </div>
          </EmptyState>
        </div>
      ) : (
        <ul className="recent-list">
          {list.map((p) => (
            <li key={p.id}>
              <Link to={`/projects/${p.id}/prds`} className="recent-item card">
                <FileTextIcon />
                <span className="recent-main">
                  <span className="recent-name truncate">{p.name}</span>
                  <span className="row row--wrap recent-meta">
                    <StatusPill view={AGGREGATE_VIEW[p.aggregate_state]} />
                    <span>{scopeLabel(t, p.progress.scope_label)}</span>
                  </span>
                </span>
                <ArrowRightIcon className="recent-chevron" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function NotFoundPage() {
  const { t } = useI18n();
  return (
    <div className="page">
      <div className="card">
        <EmptyState icon={<FileTextIcon />} title={t("notFound.title")}>
          <p>{t("notFound.body")}</p>
          <div className="empty-state-actions">
            <Link to="/" className="btn btn--primary">
              {t("nav.home")}
            </Link>
          </div>
        </EmptyState>
      </div>
    </div>
  );
}
